import type { ActivityActionType, InvestorCard } from "../../lib/api/types";

export interface ActivityFilters {
  userId: string;
  action: "" | ActivityActionType;
  from: string;
  to: string;
}

export const EMPTY_FILTERS: ActivityFilters = { userId: "", action: "", from: "", to: "" };

interface FilterBarProps {
  investors: InvestorCard[];
  value: ActivityFilters;
  onChange: (next: ActivityFilters) => void;
}

const selectClass =
  "h-9 rounded-md border border-surface-border bg-surface px-2 text-body text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

/** Investor / action / date-range filter bar (PRD §7.1). */
export function FilterBar({ investors, value, onChange }: FilterBarProps) {
  const isDirty = value.userId || value.action || value.from || value.to;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-surface-border bg-surface p-3">
      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">Investor</span>
        <select
          className={selectClass}
          value={value.userId}
          onChange={(e) => onChange({ ...value, userId: e.target.value })}
        >
          <option value="">All investors</option>
          {investors.map((inv) => (
            <option key={inv.userId} value={inv.userId}>
              {inv.displayName}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">Action</span>
        <select
          className={selectClass}
          value={value.action}
          onChange={(e) => onChange({ ...value, action: e.target.value as ActivityFilters["action"] })}
        >
          <option value="">All actions</option>
          <option value="open">Opened</option>
          <option value="download">Downloaded</option>
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">From</span>
        <input
          type="date"
          className={selectClass}
          value={value.from}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">To</span>
        <input
          type="date"
          className={selectClass}
          value={value.to}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
        />
      </label>

      {isDirty ? (
        <button
          type="button"
          onClick={() => onChange(EMPTY_FILTERS)}
          className="h-9 rounded-md px-2 text-body font-medium text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}
